import ts, { factory } from "typescript";
import _ from "lodash";
import { isReference, resolve } from "@oazapfts/resolve";
import { OazapftsContext } from "../../context";
import * as OpenApi from "../../helpers/openApi3-x";
import * as cg from "../tscodegen";
import * as h from "../../helpers";
import { getEmptySchemaType } from "../../helpers/emptySchemaType";
import { getRefAlias } from "../getRefAlias";
import { getUnionType } from "./getUnionType";
import { getTypeFromProperties } from "./getTypeFromProperties";
import { getTrueEnum } from "./getTrueEnum";
import { getAsConstEnum } from "./getAsConstEnum";
import { getTypeFromEnum } from "./getTypeFromEnum";
import { getDiscriminatorType } from "./getDiscriminatorType";

/**
 * Creates a type node from a given schema.
 * Delegates to getBaseTypeFromSchema internally and
 * optionally adds a union with null.
 */
export function getTypeFromSchema(
  ctx: OazapftsContext,
  schema?: OpenApi.SchemaObject | OpenApi.ReferenceObject,
  name?: string,
) {
  const type = getBaseTypeFromSchema(ctx, schema, name);
  return h.isNullable(schema)
    ? ts.factory.createUnionTypeNode([type, cg.keywordType.null])
    : type;
}

/**
 * This is the very core of the OpenAPI to TS conversion - it takes a
 * schema and returns the appropriate type.
 */
function getBaseTypeFromSchema(
  ctx: OazapftsContext,
  schema?: OpenApi.SchemaObject | OpenApi.ReferenceObject,
  name?: string,
): ts.TypeNode {
  if (schema === undefined) return getEmptySchemaType(ctx);
  if (isReference(schema)) {
    return getRefAlias(schema, ctx) as ts.TypeReferenceNode;
  }

  if (schema === true) {
    return getEmptySchemaType(ctx);
  }

  if (schema === false) {
    return cg.keywordType.never;
  }

  if (schema.oneOf) {
    const clone = { ...schema };
    delete clone.oneOf;
    // oneOf -> union
    return getUnionType(
      schema.oneOf.map((variant) =>
        // ensure that base properties from the schema are included in the oneOf variants
        _.mergeWith({}, clone, variant, (objValue, srcValue) => {
          if (_.isArray(objValue)) {
            return objValue.concat(srcValue);
          }
        }),
      ),
      ctx,
      schema.discriminator,
    );
  }
  if (schema.anyOf) {
    // anyOf -> union
    return getUnionType(schema.anyOf, ctx);
  }
  if (schema.discriminator?.mapping) {
    // discriminating schema -> union
    const mapping = schema.discriminator.mapping;
    return getUnionType(
      Object.values(mapping).map((ref) => ({ $ref: ref })),
      ctx,
      undefined,
    );
  }
  if (schema.allOf) {
    // allOf -> intersection
    const types: ts.TypeNode[] = [];
    for (const childSchema of schema.allOf) {
      if (
        isReference(childSchema) &&
        ctx.discriminatingSchemas.has(
          resolve(childSchema, ctx) as OpenApi.SchemaObject,
        )
      ) {
        const discriminatingSchema =
          resolve<OpenApi.UNSTABLE_DiscriminatingSchemaObject>(
            childSchema,
            ctx,
          );
        const discriminator = discriminatingSchema.discriminator;

        const matches = Object.entries(discriminator.mapping ?? {})
          .filter(([, ref]) => resolve({ $ref: ref }, ctx) === schema)
          .map(([discriminatorValue]) => discriminatorValue);
        if (matches.length > 0) {
          types.push(
            ts.factory.createTypeLiteralNode([
              cg.createPropertySignature({
                name: discriminator.propertyName,
                type: getDiscriminatorType(
                  ctx,
                  childSchema,
                  discriminator.propertyName,
                  matches,
                ),
              }),
            ]),
          );
        }
        types.push(
          getRefAlias(childSchema, ctx, /* ignoreDiscriminator */ true),
        );
      } else {
        types.push(
          getTypeFromSchema(ctx, {
            required: schema.required,
            ...childSchema,
          }),
        );
      }
    }

    if (schema.properties || schema.additionalProperties) {
      // properties -> literal type
      types.push(
        getTypeFromProperties(
          schema.properties || {},
          ctx,
          schema.required,
          schema.additionalProperties,
        ),
      );
    }
    return ts.factory.createIntersectionTypeNode(types);
  }
  // Union types defined by an array in schema.type
  if (Array.isArray(schema.type)) {
    return factory.createUnionTypeNode(
      schema.type.map((type) => {
        const subSchema = { ...schema, type } as Exclude<
          OpenApi.SchemaObject,
          boolean
        >;
        // Remove items if the type isn't array since it's not relevant
        if ("items" in subSchema && type !== "array") {
          delete subSchema.items;
        }
        if ("properties" in subSchema && type !== "object") {
          delete subSchema.properties;
        }

        return getBaseTypeFromSchema(ctx, subSchema, name);
      }),
    );
  }
  if ("items" in schema) {
    const schemaItems = schema.items;

    // items -> array of enums or unions
    if (schemaItems && !isReference(schemaItems) && schemaItems.enum) {
      const enumStyle = h.getEnumStyle(ctx.opts);
      let enumType;
      if (enumStyle !== "union" && h.isNamedEnumSchema(schemaItems, name)) {
        enumType =
          enumStyle === "as-const"
            ? getAsConstEnum(schemaItems, name, ctx)
            : getTrueEnum(schemaItems, name, ctx);
      } else {
        enumType = cg.createEnumTypeNode(schemaItems.enum);
      }

      return factory.createArrayTypeNode(enumType);
    }

    // items -> array
    return ts.factory.createArrayTypeNode(getTypeFromSchema(ctx, schema.items));
  }
  if ("prefixItems" in schema && Array.isArray(schema.prefixItems)) {
    // prefixItems -> typed tuple
    return ts.factory.createTupleTypeNode(
      schema.prefixItems.map((schema) => getTypeFromSchema(ctx, schema)),
    );
  }
  if (schema.properties || schema.additionalProperties) {
    // properties -> literal type
    return getTypeFromProperties(
      schema.properties || {},
      ctx,
      schema.required,
      schema.additionalProperties,
    );
  }

  if (schema.enum) {
    const enumStyle = h.getEnumStyle(ctx.opts);
    if (enumStyle !== "union" && h.isNamedEnumSchema(schema, name)) {
      return enumStyle === "as-const"
        ? getAsConstEnum(schema, name, ctx)
        : getTrueEnum(schema, name, ctx);
    }
    return cg.createEnumTypeNode(schema.enum);
  }
  if (schema.format == "binary") {
    return ts.factory.createTypeReferenceNode("Blob", []);
  }
  if ("const" in schema && schema.const) {
    return getTypeFromEnum([schema.const]);
  }
  if (schema.type !== undefined) {
    if (schema.type === null) return cg.keywordType.null;
    if (isKeyOfKeywordType(schema.type)) return cg.keywordType[schema.type];
  }

  return getEmptySchemaType(ctx);
}

function isKeyOfKeywordType(key: string): key is keyof typeof cg.keywordType {
  return key in cg.keywordType;
}
